import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';  // Add BehaviorSubject import
import { jwtDecode } from 'jwt-decode';

import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class JWTTokenService {

  private jwtToken: string = '';
  private decodedToken: { [key: string]: any } | null = null;  // Allow any for decoded claims

  // Reactive user profile: Starts with null (logged out), emits updates on login/logout
  private userProfileSubject = new BehaviorSubject<any>(null);
  public userProfile$: Observable<any> = this.userProfileSubject.asObservable();

  constructor(public auth: ApiService) {
    // Initialize from localStorage on service creation (handles page refresh)
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    if (typeof window !== 'undefined') {
      const storedToken = localStorage.getItem('GProToken');
      if (storedToken) {
        try {
          this.jwtToken = JSON.parse(storedToken);
          this.updateUserProfile();  // Decode and emit
        } catch (error) {
          console.error('Invalid stored token:', error);
          this.clearToken();  // Invalid token? Log out
        }
      }
    }
  }

  setToken(token: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('GProToken', JSON.stringify(token));
    }
    this.jwtToken = token;
    this.updateUserProfile();  // Decode and emit the new profile
  }

  private updateUserProfile(): void {
    if (this.jwtToken) {
      try {
        this.decodedToken = jwtDecode(this.jwtToken);
        this.userProfileSubject.next(this.decodedToken);  // Emit to subscribers
      } catch (error) {
        console.error('Token decode error:', error);
        this.userProfileSubject.next(null);  // Invalid token? Treat as logged out
      }
    } else {
      this.userProfileSubject.next(null);
    }
  }

  // For backward compatibility: Synchronous getters
  decodeToken(): void {
    if (this.jwtToken) {
      this.decodedToken = jwtDecode(this.jwtToken);
    }
  }

  getDecodeToken() {
    return this.decodedToken || (this.jwtToken ? jwtDecode(this.jwtToken) : null);
  }

  getUser(): string | null {
    this.decodeToken();
    return this.decodedToken?.['full_name'] || null;
  }

  getEmailId(): string | null {
    this.decodeToken();
    return this.decodedToken?.['email'] || null;
  }

  getExpiryTime(): number | null {
    this.decodeToken();
    return this.decodedToken?.['exp'] || null;
  }

  isTokenExpired(): boolean {
    const expiryTime = this.getExpiryTime();
    if (expiryTime) {
      return (1000 * expiryTime - Date.now()) < 5000;
    }
    return false;
  }

  // Updated: Now returns the observable (reactive)
  get userProfile(): Observable<any> {
    return this.userProfile$;
  }

  // New: Clear token and reset profile (call on logout)
  logout(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('GProToken');
      // Optionally clear other localStorage items like 'GPFullName', etc.
    }
    this.jwtToken = '';
    this.decodedToken = null;
    this.userProfileSubject.next(null);  // Emit null to show login buttons
  }

  // Helper: Clear everything (e.g., on errors)
  private clearToken(): void {
    this.logout();
  }
}